import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

/**
 * sessionStorage access can throw in blocked-storage / strict-privacy modes.
 * Never let a storage failure crash a route — degrade to "no marker" behaviour.
 */
function readFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}
function writeFlag(key: string): void {
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    /* storage unavailable — best effort only */
  }
}
function clearFlag(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable — best effort only */
  }
}

/**
 * A failed dynamic import for a *missing/stale chunk* rejects with a fetch/MIME
 * error. Genuine runtime errors thrown while a chunk *evaluates* are real code
 * bugs — reloading would waste a navigation and mask the error — so we only
 * self-heal the former and let the latter fall through to the error boundary.
 */
function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return (
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /failed to load module script/i.test(msg) ||
    /importing a module script failed/i.test(msg) ||
    /unable to preload css/i.test(msg)
  );
}

/**
 * Like React.lazy, but self-heals the "stale chunk after deploy" failure.
 *
 * When a new build deploys, the just-loaded index bundle references hashed
 * chunk filenames from that build. A browser tab that loaded the *previous*
 * index (or a CDN edge still serving it) then requests a chunk that no longer
 * exists on the server; the SPA fallback returns index.html (text/html), the
 * dynamic import rejects, and the whole route white-screens through the app
 * error boundary ("Something went wrong").
 *
 * On the first import failure for a given chunk we force one full reload so the
 * browser re-fetches the current index + chunk manifest (index.html is served
 * must-revalidate, so this picks up the new hashes). A sessionStorage guard
 * ensures we reload at most once per chunk — a genuinely broken import (offline,
 * real 5xx) then falls through to the error boundary instead of looping.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  key: string,
): LazyExoticComponent<T> {
  const flag = `chunk-reload:${key}`;
  return lazy(async () => {
    try {
      const mod = await factory();
      // Loaded cleanly — clear any prior reload marker so a future deploy in
      // this same tab session can trigger its own one-shot reload.
      clearFlag(flag);
      return mod;
    } catch (err) {
      // Only self-heal missing/stale chunks; let real runtime errors surface.
      if (isChunkLoadError(err) && !readFlag(flag)) {
        writeFlag(flag);
        window.location.reload();
        // Keep Suspense pending until the reload navigates away.
        return await new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
